import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, Paper, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { MdArrowBack, MdGroups, MdNotificationsActive, MdSchedule, MdSend, MdTune } from 'react-icons/md';
import { getData, postData } from '../../../Webservice/Webservice';
import { FONT, authToken } from '../Email/emailShared';

const TITLE_MAX = 65;
const BODY_MAX = 240;

const Section = ({ n, title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a', lineHeight: 1.2 }}>{n}. {title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

// How the notification looks on a phone's lock screen.
export const PushPreview = ({ title, body, imageUrl }) => (
  <Box sx={{ background: 'linear-gradient(160deg,#1e293b,#0f172a)', borderRadius: '24px', p: 2.5 }}>
    <Typography sx={{ fontFamily: FONT, color: '#94a3b8', fontSize: 11, textAlign: 'center', mb: 1.5 }}>Lock screen preview</Typography>
    <Box sx={{ background: 'rgba(255,255,255,.95)', borderRadius: '16px', p: 1.5, boxShadow: '0 6px 20px rgba(0,0,0,.35)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box sx={{ width: 16, height: 16, borderRadius: '5px', background: 'rgb(255 209 2)', display: 'grid', placeItems: 'center' }}><MdNotificationsActive size={11} /></Box>
        <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#64748b', fontWeight: 700 }}>GLAM COMPANY · now</Typography>
      </Box>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color: title ? '#0f172a' : '#94a3b8', wordBreak: 'break-word' }}>{title || 'Notification title'}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: body ? '#334155' : '#94a3b8', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>{body || 'Your message appears here'}</Typography>
      {imageUrl && /^https:\/\//i.test(imageUrl) && (
        <Box component="img" src={imageUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} sx={{ width: '100%', maxHeight: 150, objectFit: 'cover', borderRadius: '10px', mt: 1 }} />
      )}
    </Box>
  </Box>
);

const CITY_LIMIT = 50;

function PushForm({ onBack, onCreated, source = null }) {
  const [name, setName] = useState(source ? `${source.name} (copy)` : '');
  const [title, setTitle] = useState(source?.title || '');
  const [body, setBody] = useState(source?.body || '');
  const [imageUrl, setImageUrl] = useState(source?.imageUrl || '');
  const [link, setLink] = useState(source?.link || '');
  const [audienceType, setAudienceType] = useState(source?.audience?.type || 'all');
  const [platforms, setPlatforms] = useState(source?.audience?.platforms || []);
  const [cities, setCities] = useState(source?.audience?.cities || []);
  const [cityOptions, setCityOptions] = useState([]);
  const [picked, setPicked] = useState([]); // [{ _id, name, mobile }]
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [mode, setMode] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [sending, setSending] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    getData('campaign/city/list', null, authToken(), false).then((r) => setCityOptions(r?.cities || [])).catch(() => setCityOptions([]));
  }, []);

  const pickedIds = picked.map((p) => p._id);
  const audience = { type: audienceType, platforms, cities: cities.slice(0, CITY_LIMIT), customerIds: pickedIds };

  // how many devices this audience reaches, refreshed as the audience changes
  useEffect(() => {
    const my = ++seq.current;
    setCounting(true);
    const t = setTimeout(async () => {
      try {
        const r = await postData('push-campaign/audience', audience, authToken(), false, false);
        if (my === seq.current) setCount(r?.success ? r : null);
      } catch { if (my === seq.current) setCount(null); }
      finally { if (my === seq.current) setCounting(false); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceType, platforms.join(','), cities.join('|'), pickedIds.join(',')]);

  const searchCustomers = useCallback(async (text) => {
    setSearching(true);
    try {
      const r = await getData('push-campaign/customers', { search: text }, authToken(), false);
      setOptions(r?.customers || []);
    } catch { setOptions([]); } finally { setSearching(false); }
  }, []);
  useEffect(() => { if (audienceType === 'selected') searchCustomers(''); }, [audienceType, searchCustomers]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Give the campaign a name';
    if (!title.trim()) e.title = 'Write the title';
    if (!body.trim()) e.body = 'Write the message';
    if (imageUrl.trim() && !/^https:\/\/\S+$/i.test(imageUrl.trim())) e.imageUrl = 'Use a full https:// image link';
    if (link.trim() && !/^(https?:\/\/|[a-z][\w-]*:\/\/|\/)\S*$/i.test(link.trim())) e.link = 'Start with https://, an app link or /';
    if (audienceType === 'selected' && !picked.length) e.audience = 'Pick at least one customer';
    if (mode === 'schedule') {
      if (!scheduledAt) e.scheduledAt = 'Choose when to send';
      else if (new Date(scheduledAt).getTime() < Date.now()) e.scheduledAt = 'That time has passed';
    }
    if (count && count.devices === 0) e.audience = 'No devices can receive this yet';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async () => {
    setSubmitError('');
    if (!validate()) return;
    setSending(true);
    try {
      const r = await postData('push-campaign/create', {
        name: name.trim(), title: title.trim(), body: body.trim(), imageUrl: imageUrl.trim(), link: link.trim(), audience,
        scheduledAt: mode === 'schedule' ? new Date(scheduledAt).toISOString() : null,
      }, authToken(), false, false);
      if (!r?.success) throw new Error(r?.message || 'Could not create the campaign');
      onCreated(r.message);
    } catch (err) {
      setSubmitError(err.message || 'Could not create the campaign');
    } finally {
      setSending(false);
    }
  };

  const togglePlatform = (p) => setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <IconButton onClick={onBack} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>New push notification</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Sent to the mobile app through Firebase</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 320px' }, alignItems: 'start' }}>
        <Box sx={{ display: 'grid', gap: 2.5, minWidth: 0 }}>
          <Section n={1} title="Message" hint="What customers see on their phone" accent="#6366f1" icon={MdNotificationsActive}>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField size="small" label="Campaign name (only you see this) *" value={name} onChange={(e) => setName(e.target.value)} error={!!errors.name} helperText={errors.name} />
              <TextField size="small" label="Title *" value={title} inputProps={{ maxLength: TITLE_MAX }} onChange={(e) => setTitle(e.target.value)} error={!!errors.title} helperText={errors.title || `${title.length}/${TITLE_MAX}`} />
              <TextField size="small" label="Message *" multiline minRows={3} value={body} inputProps={{ maxLength: BODY_MAX }} onChange={(e) => setBody(e.target.value)} error={!!errors.body} helperText={errors.body || `${body.length}/${BODY_MAX}`} />
              <TextField size="small" label="Image link (optional)" placeholder="https://…/banner.jpg" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} error={!!errors.imageUrl} helperText={errors.imageUrl || 'Shown big under the message. Must be a public https:// link.'} />
              <TextField size="small" label="Opens when tapped (optional)" placeholder="https://… or /product/123" value={link} onChange={(e) => setLink(e.target.value)} error={!!errors.link} helperText={errors.link || 'Sent to the app as `link`. The app decides which screen it opens.'} />
            </Box>
          </Section>

          <Section n={2} title="Audience" hint="Who gets it. Only customers with the app who allow notifications are reached." accent="#10b981" icon={MdGroups}>
            <RadioGroup row value={audienceType} onChange={(e) => { setAudienceType(e.target.value); setErrors((x) => ({ ...x, audience: undefined })); }}>
              <FormControlLabel value="all" control={<Radio size="small" />} label="Everyone with the app" />
              <FormControlLabel value="selected" control={<Radio size="small" />} label="Chosen customers" />
            </RadioGroup>

            {audienceType === 'all' ? (
              <Box sx={{ display: 'grid', gap: 2, mt: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#475569' }}>Phones:</Typography>
                  <FormControlLabel control={<Checkbox size="small" checked={platforms.includes('android')} onChange={() => togglePlatform('android')} />} label="Android" />
                  <FormControlLabel control={<Checkbox size="small" checked={platforms.includes('ios')} onChange={() => togglePlatform('ios')} />} label="iPhone" />
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{platforms.length ? '' : 'None ticked means both'}</Typography>
                </Box>
                <Autocomplete multiple size="small" options={cityOptions} value={cities} onChange={(_, v) => setCities(v)} renderInput={(p) => <TextField {...p} label="Only these cities (optional)" helperText={cities.length > CITY_LIMIT ? `Only the first ${CITY_LIMIT} cities are used` : ' '} />} />
              </Box>
            ) : (
              <Box sx={{ mt: 1.5 }}>
                <Autocomplete
                  multiple size="small" options={options} loading={searching} value={picked} filterOptions={(x) => x} disableCloseOnSelect
                  getOptionLabel={(o) => `${o.name || 'Customer'}${o.mobile ? ` · ${o.mobile}` : ''}`} isOptionEqualToValue={(o, v) => o._id === v._id}
                  onInputChange={(_, v, reason) => { if (reason === 'input') searchCustomers(v); }}
                  onChange={(_, v) => { setPicked(v); setErrors((x) => ({ ...x, audience: undefined })); }}
                  renderTags={(v, getTagProps) => v.map((o, i) => <Chip size="small" label={o.name || o.mobile} {...getTagProps({ index: i })} key={o._id} />)}
                  renderInput={(p) => <TextField {...p} label="Search by name, mobile or email" InputProps={{ ...p.InputProps, endAdornment: <>{searching ? <CircularProgress size={16} /> : null}{p.InputProps.endAdornment}</> }} />}
                />
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mt: 0.75 }}>Handy for sending yourself a test first.</Typography>
              </Box>
            )}

            <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', background: count && count.devices === 0 ? '#fef2f2' : '#f0fdf4', border: `1px solid ${count && count.devices === 0 ? '#fecaca' : '#bbf7d0'}` }}>
              {counting && !count ? <Typography sx={{ fontFamily: FONT, fontSize: 13 }}>Counting…</Typography> : count ? (
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: count.devices === 0 ? '#991b1b' : '#166534' }}>
                  This reaches {count.devices.toLocaleString('en-IN')} device{count.devices === 1 ? '' : 's'} of {count.customers.toLocaleString('en-IN')} customer{count.customers === 1 ? '' : 's'}
                  {count.byPlatform ? ` (${Object.entries(count.byPlatform).map(([k, v]) => `${v} ${k === 'ios' ? 'iPhone' : k}`).join(', ')})` : ''}
                </Typography>
              ) : <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Could not count the audience.</Typography>}
            </Box>
            {errors.audience && <Typography sx={{ color: '#dc2626', fontSize: 12.5, mt: 1, fontFamily: FONT }}>{errors.audience}</Typography>}
          </Section>

          <Section n={3} title="When" hint="Send right away or pick a time" accent="#f59e0b" icon={MdSchedule}>
            <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
              <FormControlLabel value="now" control={<Radio size="small" />} label="Send now" />
              <FormControlLabel value="schedule" control={<Radio size="small" />} label="Schedule" />
            </RadioGroup>
            {mode === 'schedule' && (
              <TextField size="small" type="datetime-local" label="Send at" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} InputLabelProps={{ shrink: true }} error={!!errors.scheduledAt} helperText={errors.scheduledAt} sx={{ mt: 1.5, minWidth: 260 }} />
            )}
          </Section>

          {submitError && <Alert severity="error">{submitError}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button onClick={onBack} disabled={sending} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
            <Button onClick={submit} disabled={sending} startIcon={sending ? <CircularProgress size={16} /> : <MdSend />} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: '#000', '&:hover': { background: 'rgb(235 190 0)' } }}>
              {mode === 'schedule' ? 'Schedule notification' : 'Send now'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
          <PushPreview title={title} body={body} imageUrl={imageUrl.trim()} />
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start', color: '#64748b' }}>
            <MdTune size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography sx={{ fontFamily: FONT, fontSize: 12 }}>Phones may shorten long titles and messages. Keep the first line short.</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default PushForm;
